/**
 * File intake primitives shared by every upload path (initial upload, re-upload, client-side
 * upload-array). Everything here is role-agnostic plumbing: resolve pending files to file ids, turn
 * a file (or an already-persisted file id) into image ids, and persist those image ids onto the exam
 * for a given role. The role-specific pipelines (split, structure, MS, GS, answers) sit on top.
 */

import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import i18next from 'i18next';
import { readFile } from 'node:fs/promises';
import '../../i18n.js';
import { deletePendingFileMutation } from './pending-file.js';
import { updateExamArrayMutation } from './update-exam.js';
import { isEncryptedPdfError, isUserFacingUploadError, UserFacingUploadError } from './upload-errors.js';

/** The four things a teacher can upload. */
export type UploadRole = 'submissions' | 'masterSolution' | 'gradingScheme' | 'blankExam';

// ✅ FIX: Appended 'string & {}' to keep IDE literal autocomplete working without widening the union to a raw generic string
export type UploadType = UploadRole | (string & {});

/**
 * Normalizes the `uploadType` string to a role. Historically callers passed either the camelCase key
 * or a display label ('Master Solution …'), so both are accepted. Returns null for anything unknown —
 * callers then persist nothing, which is the previous behaviour.
 */
export function resolveUploadRole(uploadType: UploadType | null | undefined): UploadRole | null {
  const value = typeof uploadType === 'string' ? uploadType : '';
  if (value === 'submissions' || value.startsWith('Submissions')) return 'submissions';
  if (value === 'masterSolution' || value.startsWith('Master Solution')) return 'masterSolution';
  if (value === 'gradingScheme' || value.startsWith('Grading Scheme')) return 'gradingScheme';
  if (value === 'blankExam' || value.startsWith('Blank Exam')) return 'blankExam';
  return null;
}

export function uploadLogPrefix(role: UploadRole | null): string {
  if (role === 'masterSolution') return '[MS-Upload]';
  if (role === 'gradingScheme') return '[GS-Upload]';
  return '[File-Upload]';
}

export function normalizeFileName(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

// --- pending files -------------------------------------------------------------------------------

/**
 * Which pending entries a cancel request targets. An empty `cancelNames` targets ALL of them (the
 * "cancel the whole upload" case). Matching is lenient (exact or substring) because the agent supplies
 * names it read from the chat.
 *
 * `protectedPendingIds` are never targeted. One call can cancel some files and assign others ("die
 * leere Klausur brauche ich nicht, verarbeite aber das Bewertungsschema"), and a blanket cancel in such
 * a call means "the rest" — it must not delete the very file the same call asks to process.
 */
export function selectPendingCancelTargets(
  pendingFiles: any[],
  cancelNames: string[] = [],
  protectedPendingIds?: Set<string>,
): any[] {
  const normalizedCancelNames = cancelNames.map(name => normalizeFileName(name)).filter(Boolean);

  return (Array.isArray(pendingFiles) ? pendingFiles : []).filter(file => {
    if (protectedPendingIds?.has(String(file?.id || ''))) return false;
    if (normalizedCancelNames.length === 0) return true;
    const currentName = normalizeFileName(file?.name);
    return normalizedCancelNames.some(name => currentName === name || currentName.includes(name));
  });
}

/** Deletes the given pending entries. Returns how many were removed. */
export async function deletePendingFileEntries(examId: string, entries: any[]): Promise<number> {
  const pendingIdsToDelete = (Array.isArray(entries) ? entries : [])
    .map(file => String(file?.id || ''))
    .filter(Boolean);

  for (const pendingId of pendingIdsToDelete) {
    try {
      await deletePendingFileMutation(examId, pendingId)();
    } catch (err) {
      logger.warn({ err, pendingId, examId }, 'deletePendingFiles: Pending-Datei konnte nicht gelöscht werden.');
    }
  }

  return pendingIdsToDelete.length;
}

/**
 * Deletes pending files by name. An empty `cancelNames` deletes ALL pending files of the exam (the
 * "cancel the whole upload" case). Returns the number of pending entries removed.
 */
export async function deletePendingFiles(
  examId: string,
  pendingFiles: any[],
  cancelNames: string[] = [],
): Promise<number> {
  return deletePendingFileEntries(examId, selectPendingCancelTargets(pendingFiles, cancelNames));
}

/**
 * A pending reference the agent may supply as either a plain filename or a `<pendingId>:<name>`
 * token (the assignment panel sends the latter so a re-uploaded, identically named file still maps
 * to the right pending entry). The name part is percent-decoded.
 */
function parsePendingReference(raw: string): { pendingId?: string; name: string } {
  const token = String(raw || '').trim();
  if (!token) return { name: '' };

  const separatorIndex = token.indexOf(':');
  if (separatorIndex > 0) {
    const pendingId = token.slice(0, separatorIndex).trim();
    const encodedName = token.slice(separatorIndex + 1).trim();
    let decodedName = encodedName;
    try {
      decodedName = encodedName ? decodeURIComponent(encodedName) : '';
    } catch {
      decodedName = encodedName;
    }

    if (pendingId) return { pendingId, name: decodedName || encodedName };
  }

  return { name: token };
}

export interface PendingFileResolver {
  /** File ids of every pending entry matching the requested names, in request order. */
  resolveMany(requested: string[] | undefined): string[];
  /** Convenience for the single-file roles (MS / GS / blank exam). */
  resolveOne(requested: string | undefined): string[];
  /** Pending entry ids consumed so far — delete these once the upload succeeded. */
  readonly usedPendingIds: Set<string>;
}

/**
 * Maps requested filenames to the file ids of the exam's pending entries. Each pending entry is
 * consumed at most once, so two files with the same name still resolve to two distinct entries.
 * Newest entries win (the list is searched back-to-front) — a re-upload of the same name supersedes
 * the older one.
 *
 * `parsePendingIdTokens` enables `<pendingId>:<name>` resolution; leave it off for callers that only
 * ever receive plain filenames, so a filename containing a colon can't be misread as a token.
 */
export function createPendingFileResolver(
  pendingFiles: any[],
  options?: { parsePendingIdTokens?: boolean },
): PendingFileResolver {
  const entries = Array.isArray(pendingFiles) ? pendingFiles : [];
  const usedPendingIds = new Set<string>();
  const parseTokens = options?.parsePendingIdTokens === true;

  const resolveMany = (requested: string[] | undefined): string[] => {
    if (!Array.isArray(requested) || requested.length === 0) return [];

    const resolved: string[] = [];
    for (const raw of requested) {
      const reference = parseTokens ? parsePendingReference(raw) : { pendingId: undefined, name: String(raw || '') };
      const requestedName = normalizeFileName(reference.name);
      if (!requestedName) continue;

      let found =
        reference.pendingId && !usedPendingIds.has(reference.pendingId)
          ? entries.find(entry => String(entry?.id || '') === reference.pendingId)
          : null;

      if (!found) {
        found = entries
          .slice()
          .reverse()
          .find(
            entry =>
              !usedPendingIds.has(String(entry?.id || '')) &&
              (normalizeFileName(entry?.name) === requestedName ||
                normalizeFileName(entry?.name).includes(requestedName)),
          );
      }

      if (found && Array.isArray(found.fileIds) && found.fileIds.length > 0) {
        found.fileIds.forEach((id: unknown) => {
          if (typeof id === 'string' && id.trim()) resolved.push(id);
        });
        if (found.id) usedPendingIds.add(String(found.id));
      } else {
        logger.warn({ raw }, 'Kein passender Pending-Eintrag für Dateiname gefunden, überspringe.');
      }
    }

    return resolved;
  };

  return {
    resolveMany,
    resolveOne: (requested: string | undefined) => {
      const value = typeof requested === 'string' ? requested.trim() : '';
      return value ? resolveMany([value]) : [];
    },
    usedPendingIds,
  };
}

// --- files → image ids ---------------------------------------------------------------------------

export const extractImagesFromPDFMutation = registerMutation({
  operationName: 'extractImagesFromPDFMutation',
  type: () => [String],
  params: [{ name: 'pdfId', type: () => String }],
  resolve: (pdfId: string) => async () => {
    logger.info({ pdfId, context: 'PDF-Extraktion' }, 'Mutation gestartet');
    try {
      const { extractImageFromPDF } = await import('../utils/transform-file.js');
      const imageIds = await extractImageFromPDF(pdfId);

      if (!Array.isArray(imageIds)) {
        logger.error(
          { context: 'PDF-Extraktion', returnType: typeof imageIds, returnValue: JSON.stringify(imageIds) },
          'extractImageFromPDF hat kein Array zurueckgegeben',
        );
        return [];
      }

      if (imageIds.length === 0) {
        logger.warn({ pdfId, context: 'PDF-Extraktion' }, 'PDF hat keine Seiten oder Konvertierung fehlgeschlagen');
        return [];
      }

      logger.info(
        { imageCount: imageIds.length, pdfId, context: 'PDF-Extraktion' },
        'Mutation erfolgreich: Bilder aus PDF extrahiert',
      );
      return imageIds;
    } catch (err) {
      logger.error(
        { pdfId, errMessage: err instanceof Error ? err.message : String(err), context: 'PDF-Extraktion' },
        'Fehler in extractImagesFromPDFMutation',
      );
      if (isEncryptedPdfError(err)) {
        const userMessage = i18next.t('upload.encryptedPdfError', {
          defaultValue:
            'Eine hochgeladene PDF ist verschlüsselt und konnte nicht verarbeitet werden. Bitte entsperre die PDF (Passwortschutz entfernen) und lade sie erneut hoch.',
        });
        throw new UserFacingUploadError('PDF_ENCRYPTED', userMessage, err instanceof Error ? err.message : String(err));
      }
      throw err;
    }
  },
});

/** Loads already-persisted PDF/Image ids back into File objects (for the split agent). */
export async function resolveFileIdsToFiles(fileIds: string[]): Promise<File[]> {
  const projection = { _id: 1, createdAt: 1, meta: { extension: 1, fileName: 1, mimetype: 1, size: 1 } } as const;
  const resolvedFiles: File[] = [];

  for (const rawId of fileIds) {
    const fileId = String(rawId || '').trim();
    if (!fileId) continue;

    const pdfFile = await getByID(PDFFile, fileId)(projection as any);
    if (pdfFile) {
      const bytes = await readFile(pdfFile.getFullPath());
      const fileName = (pdfFile as any)?.meta?.fileName || `${fileId}.pdf`;
      const mimeType = (pdfFile as any)?.meta?.mimetype || 'application/pdf';
      const file = new File([bytes], fileName, { type: mimeType });
      (file as any).sourcePdfId = fileId;
      resolvedFiles.push(file);
      continue;
    }

    const imageFile = await getByID(ImageFile, fileId)(projection as any);
    if (imageFile) {
      const bytes = await readFile(imageFile.getFullPath());
      const fileName = (imageFile as any)?.meta?.fileName || `${fileId}.png`;
      const mimeType = (imageFile as any)?.meta?.mimetype || 'image/png';
      const file = new File([bytes], fileName, { type: mimeType });
      (file as any).sourceImageId = fileId;
      resolvedFiles.push(file);
      continue;
    }

    logger.warn({ fileId }, 'Could not resolve fileId to PDF/Image file, skipping');
  }

  return resolvedFiles;
}

/**
 * Image ids for a file that is ALREADY persisted (we only have its id). A PDF is exploded into page
 * images; anything else (or a PDF we can't explode) is used as-is. Encryption and user-facing errors
 * propagate — everything else falls back to treating the id as a single image.
 */
export async function extractImageIdsFromExistingFileId(fileId: string): Promise<string[]> {
  const existingId = String(fileId);
  try {
    const extracted = await extractImagesFromPDFMutation(existingId)();
    if (Array.isArray(extracted) && extracted.length) {
      logger.info({ imageCount: extracted.length, existingId }, 'Extracted images from provided PDF id');
      return extracted;
    }
    return [existingId];
  } catch (err) {
    if (isUserFacingUploadError(err) || isEncryptedPdfError(err)) throw err;
    return [existingId];
  }
}

/**
 * Persists a not-yet-stored file and returns its page image ids. Returns null for an unsupported
 * type so the caller can surface the "invalid file type" hint and abort.
 *
 * A split submission carries its bytes as `splitPdfDataUrl` (produced by the split agent) and a
 * re-resolved PNG carries `sourceImageId`; both skip a redundant re-upload.
 */
export async function persistFileAsImageIds(file: any, logPrefix = '[File-Upload]'): Promise<string[] | null> {
  const sourceImageId = typeof file?.sourceImageId === 'string' ? file.sourceImageId : undefined;
  const splitPdfDataUrl = typeof file?.splitPdfDataUrl === 'string' ? file.splitPdfDataUrl : '';

  if (file.type === 'application/pdf') {
    let fileId: string;
    if (splitPdfDataUrl) {
      const base64 = splitPdfDataUrl.split(',')[1] || '';
      if (!base64) {
        throw new Error('Split PDF data URL is invalid');
      }
      logger.info({ context: logPrefix, fileName: file.name }, 'Erstelle PDF aus base64');
      const created = await PDFFile.createFromBuffer(Buffer.from(base64, 'base64'), file.name || 'submission.pdf');
      fileId = String(created._id);
      logger.info({ context: logPrefix, fileId }, 'PDF erstellt');
    } else {
      logger.info({ context: logPrefix, fileName: file.name, sizeBytes: file.size }, 'Lade PDF-Datei hoch');
      fileId = await PDFFile.upload(file);
      logger.info({ context: logPrefix, fileId }, 'PDF hochgeladen');
    }

    logger.info({ context: logPrefix, fileId }, 'Starte Bildextraktion aus PDF');
    try {
      const imageIds = await extractImagesFromPDFMutation(fileId)();
      if (!imageIds || imageIds.length === 0) {
        logger.error({ context: logPrefix, fileId }, 'KRITISCH: Keine Bilder aus PDF extrahiert');
        throw new Error(`Keine Bilder aus PDF extrahiert: ${fileId}`);
      }
      logger.info({ context: logPrefix, imageCount: imageIds.length, fileId }, 'Erfolgreich Bilder aus PDF extrahiert');
      return imageIds;
    } catch (err) {
      logger.error({ err, context: logPrefix, fileId }, 'FEHLER beim Extrahieren von Bildern aus PDF');
      throw err;
    }
  }

  if (file.type === 'image/png') {
    if (sourceImageId) return [sourceImageId];
    logger.info({ context: logPrefix, fileName: file.name, sizeBytes: file.size }, 'Lade PNG-Datei hoch');
    const fileId = await ImageFile.upload(file);
    logger.info({ context: logPrefix, fileId }, 'PNG hochgeladen');
    return [fileId];
  }

  return null;
}

/**
 * Stores a picked file AS IS and returns its id — no PDF explode, no image extraction.
 *
 * The counterpart of `resolveFileIdsToFiles`, and it exists so the browser's file picker can hand the split a
 * PAYLOAD. `persistFileAsImageIds` would do the explode here, in the request, which is the core-bound work the
 * `submissionSplit` job was created to schedule; this stores the upload and stops.
 */
export async function persistUploadAsFileId(file: any, logPrefix = '[File-Upload]'): Promise<string | null> {
  if (file?.type === 'application/pdf') {
    const fileId = await PDFFile.upload(file);
    logger.info({ context: logPrefix, fileId, fileName: file?.name }, 'PDF gespeichert (ohne Extraktion)');
    return fileId;
  }
  if (file?.type === 'image/png') {
    const fileId = await ImageFile.upload(file);
    logger.info({ context: logPrefix, fileId, fileName: file?.name }, 'PNG gespeichert');
    return fileId;
  }
  return null;
}

/** Every picked file stored, skipping the ones whose type this system does not accept. */
export async function persistUploadsAsFileIds(files: any[], logPrefix = '[File-Upload]'): Promise<string[]> {
  const ids: string[] = [];
  for (const file of files) {
    const fileId = await persistUploadAsFileId(file, logPrefix);
    if (fileId) ids.push(fileId);
  }
  return ids;
}

/**
 * Attaches image ids to the exam for a non-submission role. The id array IS the record that a file was
 * uploaded — there is no separate "uploaded" flag any more (it duplicated this and a refine falsified it).
 * Submissions aren't handled here — they become Submission documents, not an exam array.
 */
export async function persistRoleArtifact(examId: string, role: UploadRole, imageIds: string[]): Promise<void> {
  if (role === 'submissions') {
    throw new Error('persistRoleArtifact: submissions are persisted as Submission documents, not on the exam');
  }

  if (role === 'masterSolution') {
    logger.info({ context: 'MS-Upload', imageCount: imageIds.length, examId }, 'Speichere Master Solution Bilder');
    await updateExamArrayMutation(examId, 'masterSolutionIds', imageIds)();
    logger.info({ context: 'MS-Upload', examId, imageIds }, 'Master Solution IDs gespeichert');
    return;
  }

  if (role === 'gradingScheme') {
    logger.info({ context: 'GS-Upload', imageCount: imageIds.length, examId }, 'Speichere Bewertungsschema Bilder');
    await updateExamArrayMutation(examId, 'gradingSchemeIds', imageIds)();
    logger.info({ context: 'GS-Upload', examId, imageIds }, 'Bewertungsschema IDs gespeichert');
    return;
  }

  await updateExamArrayMutation(examId, 'blankExamIds', imageIds)();
  logger.info({ context: 'Blank-Exam-Upload', examId, imageCount: imageIds.length }, 'Leere Klausur gespeichert');
}
